// Using pdfjs-dist v3.x for compatibility
import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker - v3.x compatible
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

export interface ConvertedPage {
  pageNumber: number;
  dataUrl: string;
  base64: string;
  mimeType: string;
}

export interface PdfConversionResult {
  originalFileName: string;
  pages: ConvertedPage[];
  totalPages: number;
}

/**
 * Convert a PDF file to an array of images (one per page)
 * @param file - The PDF file to convert
 * @param scale - Scale factor for rendering (default 2.0 for good quality)
 * @returns Promise with conversion result containing image data for each page
 */
export async function convertPdfToImages(
  file: File,
  scale: number = 2.0
): Promise<PdfConversionResult> {
  console.log(`[PDF Converter] Processing: ${file.name}`);
  
  // Read file as ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();
  
  // Load PDF document
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const totalPages = pdf.numPages;
  
  console.log(`[PDF Converter] ${file.name} has ${totalPages} pages`);
  
  const pages: ConvertedPage[] = [];
  
  // Convert each page to an image
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) {
      throw new Error('Failed to get canvas context');
    }
    
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    // Render page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };
    
    await page.render(renderContext).promise;
    
    // Convert to data URL (JPEG for smaller size)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const base64 = dataUrl.split(',')[1];
    
    pages.push({
      pageNumber: pageNum,
      dataUrl,
      base64,
      mimeType: 'image/jpeg'
    });
    
    console.log(`[PDF Converter] Converted page ${pageNum}/${totalPages}`);
  }
  
  return {
    originalFileName: file.name,
    pages,
    totalPages
  };
}

/**
 * Convert multiple PDF files to images
 * @param files - Array of PDF files to convert
 * @param scale - Scale factor for rendering
 * @returns Promise with array of conversion results
 */
export async function convertMultiplePdfsToImages(
  files: File[],
  scale: number = 2.0
): Promise<PdfConversionResult[]> {
  const results: PdfConversionResult[] = [];
  
  for (const file of files) {
    if (file.type === 'application/pdf') {
      try {
        const result = await convertPdfToImages(file, scale);
        results.push(result);
      } catch (error) {
        console.error(`[PDF Converter] Failed to convert ${file.name}:`, error);
      }
    }
  }
  
  return results;
}

/**
 * Check if a file is a PDF
 */
export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Check if a file is an image
 */
export function isImage(file: File): boolean {
  return file.type.startsWith('image/');
}
